export interface SpacingValue {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface ResponsiveStyles {
  tablet?: Partial<BlockStyles>;
  mobile?: Partial<BlockStyles>;
}

export interface BlockStyles {
  padding: SpacingValue;
  margin: SpacingValue;
  backgroundColor?: string;
  responsive?: ResponsiveStyles;
}

export interface BlockVisibility {
  desktop: boolean;
  tablet: boolean;
  mobile: boolean;
}

export interface BaseBlock {
  id: string;
  type: string;
  styles: BlockStyles;
  customCss?: string;
  visibility?: BlockVisibility;
  displayCondition?: {
    label: string;
    before: string;
    after: string;
    group?: string;
    description?: string;
  };
}

export type ColumnLayout = "1" | "2" | "3" | "2-1" | "1-2";

export interface SectionBlock extends BaseBlock {
  type: "section";
  columns: ColumnLayout;
  children: Block[][];
}

export type HeadingLevel = 1 | 2 | 3 | 4;

export const HEADING_LEVEL_FONT_SIZE: Record<HeadingLevel, number> = {
  1: 36,
  2: 28,
  3: 22,
  4: 18,
};

export interface TitleBlock extends BaseBlock {
  type: "title";
  content: string;
  level: HeadingLevel;
  color: string;
  textAlign: "left" | "center" | "right";
  fontFamily?: string;
}

export interface ParagraphBlock extends BaseBlock {
  type: "paragraph";
  content: string;
}

export interface ImageBlock extends BaseBlock {
  type: "image";
  src: string;
  alt: string;
  width: number | "full";
  align: "left" | "center" | "right";
  linkUrl?: string;
  linkOpenInNewTab?: boolean;
  placeholderUrl?: string;
  decorative?: boolean;
}

/**
 * Hosted popup / wizard behavior when the button is clicked.
 * Email/HTML runtimes may interpret these alongside {@link ButtonBlock.url}.
 */
export type ButtonClickAction =
  | "none"
  | "next_step"
  | "previous_step"
  | "load_page"
  | "scroll_anchor"
  | "close_popup";

/** Which edges receive the button outline when {@link ButtonBlock.borderWidth} &gt; 0. */
export type ButtonBorderSides = "all" | "top" | "right" | "bottom" | "left";

/** Native-like field rendered as HTML `<input>` (popups / landing pages; limited email client support). */
export type InputFieldType = "text" | "email" | "date";

export interface InputBlock extends BaseBlock {
  type: "input";
  inputType: InputFieldType;
  /** HTML `name` attribute */
  name: string;
  label: string;
  placeholder: string;
  /** Prefilled value in exported HTML */
  defaultValue?: string;
  required?: boolean;
  labelMargin: SpacingValue;
  labelPadding: SpacingValue;
  labelFontSize: number;
  labelFontFamily?: string;
  labelColor: string;
  labelFontWeight: "normal" | "bold";
  labelTextAlign: "left" | "center" | "right";
  inputMargin: SpacingValue;
  inputPadding: SpacingValue;
  inputBackgroundColor: string;
  inputTextColor: string;
  inputPlaceholderColor: string;
  inputFontSize: number;
  inputFontFamily?: string;
  inputBorderRadius: number;
  inputBorderWidth?: number;
  inputBorderColor?: string;
  inputBorderSides?: ButtonBorderSides;
  inputWidth: number | "full";
}

export interface ButtonBlock extends BaseBlock {
  type: "button";
  text: string;
  url: string;
  openInNewTab?: boolean;
  /** Omit or `"none"` for a normal link using {@link ButtonBlock.url}. */
  clickAction?: ButtonClickAction;
  /** Fragment id (without `#`) when `clickAction` is `scroll_anchor`. */
  clickActionAnchorId?: string;
  backgroundColor: string;
  textColor: string;
  borderRadius: number;
  fontSize: number;
  buttonPadding: SpacingValue;
  fontFamily?: string;
  /** Outline width in px; omit or `0` for no border. */
  borderWidth?: number;
  /** Outline color (hex); used when `borderWidth` &gt; 0. */
  borderColor?: string;
  /** Which edges show the border; defaults to `all`. */
  borderSides?: ButtonBorderSides;
}

export interface DividerBlock extends BaseBlock {
  type: "divider";
  lineStyle: "solid" | "dashed" | "dotted";
  color: string;
  thickness: number;
  width: number | "full";
}

export interface VideoBlock extends BaseBlock {
  type: "video";
  url: string;
  openInNewTab?: boolean;
  thumbnailUrl: string;
  alt: string;
  width: number | "full";
  align: "left" | "center" | "right";
  placeholderUrl?: string;
}

export type SocialPlatform =
  | "facebook"
  | "twitter"
  | "instagram"
  | "linkedin"
  | "youtube"
  | "tiktok"
  | "pinterest"
  | "email"
  | "whatsapp"
  | "telegram"
  | "discord"
  | "snapchat"
  | "reddit"
  | "github"
  | "dribbble"
  | "behance";

export type SocialIconStyle =
  | "solid"
  | "outlined"
  | "rounded"
  | "square"
  | "circle";

export type SocialIconSize = "small" | "medium" | "large";

export interface SocialIcon {
  id: string;
  platform: SocialPlatform;
  url: string;
}

export interface SocialIconsBlock extends BaseBlock {
  type: "social";
  icons: SocialIcon[];
  iconStyle: SocialIconStyle;
  iconSize: SocialIconSize;
  spacing: number;
  align: "left" | "center" | "right";
}

export interface SpacerBlock extends BaseBlock {
  type: "spacer";
  height: number;
}

export interface HtmlBlock extends BaseBlock {
  type: "html";
  content: string;
}

export interface MenuItemData {
  id: string;
  text: string;
  url: string;
  openInNewTab: boolean;
  bold: boolean;
  underline: boolean;
  color?: string;
}

export interface MenuBlock extends BaseBlock {
  type: "menu";
  items: MenuItemData[];
  fontSize: number;
  fontFamily?: string;
  color: string;
  linkColor?: string;
  textAlign: "left" | "center" | "right";
  separator: string;
  separatorColor: string;
  spacing: number;
}

export interface TableCellData {
  id: string;
  content: string;
}

export interface TableRowData {
  id: string;
  cells: TableCellData[];
}

export interface TableBlock extends BaseBlock {
  type: "table";
  rows: TableRowData[];
  hasHeaderRow: boolean;
  headerBackgroundColor?: string;
  borderColor: string;
  borderWidth: number;
  cellPadding: number;
  fontSize: number;
  fontFamily?: string;
  color: string;
  textAlign: "left" | "center" | "right";
}

export type CountdownStyle =
  | "minimal"
  | "matrix"
  | "flip"
  | "glassmorphism"
  | "neonGaming"
  | "retroLed"
  | "scifiHologram"
  | "luxuryGold"
  | "floating3d"
  | "liquidMorph"
  | "minimalDarkSaas"
  | "mechanicalCounter"
  | "gradientBlob"
  | "terminalCli"
  | "spaceGalaxy"
  | "animeCyberpunk"
  | "luxuryWedding"
  | "kidsCartoon"
  | "industrialTech"
  | "aiFuturistic";

export type CountdownLayout =
  | "horizontal"
  | "vertical"
  | "compact"
  | "fullscreen";

export type CountdownAnimationSpeed = "slow" | "normal" | "fast";

export interface CountdownBlock extends BaseBlock {
  type: "countdown";
  targetDate: string;
  timezone: string;
  showDays: boolean;
  showHours: boolean;
  showMinutes: boolean;
  showSeconds: boolean;
  separator: ":" | "-" | " ";
  digitFontSize: number;
  digitColor: string;
  labelColor: string;
  labelFontSize: number;
  backgroundColor: string;
  fontFamily?: string;
  labelDays: string;
  labelHours: string;
  labelMinutes: string;
  labelSeconds: string;
  expiredMessage: string;
  expiredImageUrl: string;
  hideOnExpiry: boolean;
  // Themed countdown variants — optional with `minimal` as the default to preserve
  // backward compatibility with the original CountdownBlock rendering.
  style?: CountdownStyle;
  layout?: CountdownLayout;
  animationSpeed?: CountdownAnimationSpeed;
  accentColor?: string;
  cardGap?: number;
  cardRadius?: number;
  enableParticles?: boolean;
  enableHoverFx?: boolean;
}

export interface CustomBlock extends BaseBlock {
  type: "custom";
  customType: string;
  fieldValues: Record<string, unknown>;
  renderedHtml?: string;
  dataSourceFetched?: boolean;
}

// ---------------------------------------------------------------------------
// Form block — multi-field opt-in form with integrations, button, terms.
// ---------------------------------------------------------------------------

/** Built-in field types currently supported by {@link FormBlock}. */
export type FormFieldType = "firstName" | "lastName" | "email" | "phone";

export interface FormFieldData {
  id: string;
  type: FormFieldType;
  /** HTML `name` attribute used at submit time. */
  name: string;
  label: string;
  placeholder: string;
  required: boolean;
  /**
   * Email-only: refuse signups whose email already exists.
   */
  blockExisting?: boolean;
  /**
   * Email-only: scope the duplicate check across all campaigns or this one only.
   */
  blockExistingScope?: "all_campaigns" | "this_campaign";
  /**
   * Email-only: error shown when a duplicate email is rejected.
   */
  blockExistingMessage?: string;
}

/**
 * A marketing/CRM destination the form syncs to. Identifiers are intentionally
 * loose strings so the SDK consumer can register additional integrations
 * without needing a type bump. The editor renders a curated grid of built-ins.
 */
export type FormSyncIntegrationId = string;

/** Submit button's interaction after a successful submit. */
export type FormButtonAction =
  | "next_step"
  | "close_popup"
  | "load_page"
  | "none";

/** Submit button + label layout: side-by-side or stacked. */
export type FormButtonLayout = "one_line" | "two_lines";

export interface FormBlock extends BaseBlock {
  type: "form";
  fields: FormFieldData[];
  /** Currently selected integration (single pick); `null` means none yet. */
  syncIntegrationId: FormSyncIntegrationId | null;
  /** Submit button configuration. */
  submitButtonText: string;
  buttonAction: FormButtonAction;
  buttonLayout: FormButtonLayout;
  /** Optional URL when {@link buttonAction} is `load_page`. */
  buttonUrl?: string;
  /** Free-form terms / disclaimer HTML shown beneath the form. */
  termsContent: string;
  /** Show the terms section below the form fields. */
  termsEnabled: boolean;
  // Visual styling — mirrors InputBlock's input chrome so all fields render
  // consistently without per-field repetition.
  inputBackgroundColor: string;
  inputTextColor: string;
  inputPlaceholderColor: string;
  inputBorderColor: string;
  inputBorderWidth: number;
  inputBorderRadius: number;
  inputFontSize: number;
  inputPadding: SpacingValue;
  inputSpacing: number;
  labelColor: string;
  labelFontSize: number;
  // Submit button visuals.
  buttonBackgroundColor: string;
  buttonTextColor: string;
  buttonBorderRadius: number;
  buttonFontSize: number;
}

/**
 * How a discount is sourced when the shopper subscribes or completes the flow.
 * Runtime integrations (e.g. Shopify) interpret these settings server-side.
 */
export type DiscountCouponMode = "none" | "auto_code" | "manual_shopify";

/** Shape of the discount when {@link DiscountCouponBlock.discountMode} is `auto_code`. */
export type DiscountCouponValueType =
  | "percentage_off"
  | "fixed_amount"
  | "free_shipping";

/** What happens after the visitor successfully copies the discount code (editor preview + hosted runtime). */
export type DiscountCouponAfterCopyAction =
  | "none"
  | "load_page"
  | "close_popup"
  | "next_step";

export interface DiscountCouponBlock extends BaseBlock {
  type: "discountCoupon";
  discountMode: DiscountCouponMode;
  /** Used when `discountMode` is `auto_code`. */
  valueType: DiscountCouponValueType;
  /** Percentage (e.g. 10 → 10%) or fixed amount in {@link currencyCode}. */
  valueAmount: number;
  currencyCode: string;
  expirationEnabled: boolean;
  expirationDays: number;
  tagCustomerWithCode: boolean;
  combineProductDiscounts: boolean;
  combineOrderDiscounts: boolean;
  combineShippingDiscounts: boolean;
  /** Shopify discount code from admin; used when `discountMode` is `manual_shopify`. */
  manualDiscountCode: string;
  /** Brief message shown in the coupon UI after a successful copy (empty → runtime default). */
  copyConfirmationText: string;
  /** Follow-up behavior after copy (mirrors popup button actions where applicable). */
  afterCopyAction: DiscountCouponAfterCopyAction;
  /** Destination URL when {@link afterCopyAction} is `load_page`. */
  afterCopyUrl?: string;
}

export type Block =
  | SectionBlock
  | TitleBlock
  | ParagraphBlock
  | ImageBlock
  | InputBlock
  | ButtonBlock
  | DividerBlock
  | VideoBlock
  | SocialIconsBlock
  | SpacerBlock
  | HtmlBlock
  | MenuBlock
  | TableBlock
  | CountdownBlock
  | FormBlock
  | DiscountCouponBlock
  | CustomBlock;

export type BlockType = Block["type"];
